import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { Node, mergeAttributes } from '@tiptap/core'
import type { AnyExtension } from '@tiptap/react'

import { FallbackValuesSection } from '~components/internal/FallbackValuesSection'
import Typography from '~components/internal/Typography'
import {
  type PersonalizationVariable,
  VariableInsertButton,
  detectVariables,
} from '~components/internal/VariableComponents'

import RichTextEditorComponent, { type RichTextEditorRef } from './RichTextEditor'
import './RichTextEditorWithVariables.scss'

const EmailVariableChip = Node.create({
  name: 'emailVariableChip',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      varKey: {
        default: null,
        parseHTML: (element: HTMLElement): string | null => element.getAttribute('data-variable'),
      },
      label: {
        default: null,
        parseHTML: (element: HTMLElement): string | null => element.getAttribute('data-label'),
      },
      subField: {
        default: null,
        parseHTML: (element: HTMLElement): string | null => element.getAttribute('data-sub-field'),
        renderHTML: (attributes: Record<string, string | null>): Record<string, string> =>
          attributes.subField ? { 'data-sub-field': attributes.subField } : {},
      },
    }
  },

  parseHTML() {
    return [{ tag: 'span[data-variable]' }]
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-variable': node.attrs.varKey,
        'data-label': node.attrs.label ?? node.attrs.varKey,
        contenteditable: 'false',
        class: 'email-variable-chip',
      }),
      node.attrs.label ?? node.attrs.varKey,
    ]
  },
})

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

interface RichTextEditorWithVariablesProps {
  initialContent: string
  onChange?: (htmlContent: string) => void
  variables: PersonalizationVariable[]
  fallbacks: Record<string, string>
  onFallbacksChange: (fallbacks: Record<string, string>) => void
  label?: string
  disabled?: boolean
  extensions?: AnyExtension[]
  className?: string
  /** Whether to render the fallback values section under the editor. */
  showFallbacks?: boolean
  /** When set, clicking Insert Variable with a text selection converts the
   * selection into a variable named after it instead of opening the menu. */
  onCreateFromSelection?: (selectedText: string) => void
  insertControl?: React.ReactNode
}

export interface RichTextEditorWithVariablesRef {
  insertVariable: (varKey: string, label?: string) => void
}

const RichTextEditorWithVariables = forwardRef<
  RichTextEditorWithVariablesRef,
  RichTextEditorWithVariablesProps
>(function RichTextEditorWithVariables(
  {
    initialContent,
    onChange,
    variables,
    fallbacks,
    onFallbacksChange,
    label,
    disabled = false,
    extensions = [],
    className,
    showFallbacks = true,
    onCreateFromSelection,
    insertControl,
  },
  ref,
) {
  const richEditorRef = useRef<RichTextEditorRef>(null)
  const [currentHTML, setCurrentHTML] = useState(initialContent || '')

  useEffect(() => {
    setCurrentHTML(initialContent || '')
  }, [initialContent])

  const allExtensions = useMemo<AnyExtension[]>(
    () => [EmailVariableChip, ...extensions],
    [extensions],
  )

  const insertVariable = (varKey: string, labelOverride?: string): void => {
    const varDef = variables.find((v) => v.key === varKey)
    const label = escapeHtml(labelOverride ?? varDef?.label ?? varKey)
    richEditorRef.current?.insertHTML(
      `<span data-variable="${varKey}" data-label="${label}">${label}</span>`,
    )
  }

  useImperativeHandle(ref, () => ({ insertVariable }))

  const handleInsertVariableClick = (): boolean => {
    if (!onCreateFromSelection) return false
    const selectedText = richEditorRef.current?.getSelectedText().trim()
    if (!selectedText) return false
    onCreateFromSelection(selectedText)
    return true
  }

  const handleChange = (html: string): void => {
    setCurrentHTML(html)
    onChange?.(html)
  }

  const detectedVariables = useMemo(
    () => detectVariables(currentHTML, variables, true),
    [currentHTML, variables],
  )

  return (
    <Box>
      {(!disabled || label) && (
        <Box className='rte-with-variables__toolbar'>
          {label && <Typography className='rte-with-variables__label'>{label}</Typography>}
          {!disabled &&
            (insertControl ?? (
              <VariableInsertButton
                variables={variables}
                onVariableInsert={insertVariable}
                interceptClick={onCreateFromSelection ? handleInsertVariableClick : undefined}
              />
            ))}
        </Box>
      )}

      <RichTextEditorComponent
        ref={richEditorRef}
        initialContent={initialContent}
        onChange={handleChange}
        extensions={allExtensions}
        className={className}
      />

      {showFallbacks && detectedVariables.length > 0 && (
        <FallbackValuesSection
          variables={detectedVariables}
          fallbacks={fallbacks}
          onFallbacksChange={onFallbacksChange}
          className='rte-with-variables__fallbacks'
        />
      )}
    </Box>
  )
})

export default RichTextEditorWithVariables
